/*
 * @Author: chi.yong chi.yong@jitai.ai
 * @Date: 2024-11-11 17:34:08
 * @LastEditors: chi.yong chi.yong@jitai.ai
 * @LastEditTime: 2025-03-23 00:00:00
 * @FilePath: /JTFE_home_user/src/views/stock/hooks/tickerHighchart.ts
 * @Description:
 *
 * Copyright (c) 2024 by ${git_name_email}, All Rights Reserved.
 */
import highcharts, { type AxisLabelsFormatterContextObject, type Options } from 'highcharts';
import 'highcharts/highcharts-more';
import 'highcharts/indicators/indicators';
import 'highcharts/indicators/macd';
import 'highcharts/indicators/bollinger-bands';
import 'highcharts/indicators/price-channel';
import 'highcharts/indicators/rsi';
import 'highcharts/indicators/stochastic';
import 'highcharts/indicators/williams-r';
import 'highcharts/modules/accessibility';
import 'highcharts/modules/annotations-advanced';
import 'highcharts/modules/drag-panes';
import 'highcharts/modules/full-screen';
import 'highcharts/modules/sankey';
import 'highcharts/modules/solid-gauge.js';
import 'highcharts/modules/stock';
import 'highcharts/modules/stock-tools';
import 'highcharts/modules/price-indicator';

import en from '@/locales/en.js';
import zh from '@/locales/zh.js';
import { logoColors } from '@/utils/chartStyle';

type LanguageCode = 'zh' | 'en';

interface TickerHighChartMessages {
  companyName: string;
  all: string;
  from: string;
  to: string;
  zoom: string;
  january: string;
  february: string;
  march: string;
  april: string;
  may: string;
  june: string;
  july: string;
  august: string;
  september: string;
  october: string;
  november: string;
  december: string;
  sunday: string;
  monday: string;
  tuesday: string;
  wednesday: string;
  thursday: string;
  friday: string;
  saturday: string;
  month: string;
  month3: string;
  month6: string;
  yearToDate: string;
  year1: string;
}

const dateTimeLabelFormats = {
  year: '%Y',
  month: '%Y-%m',
  week: '%Y-%m-%d',
  day: '%Y-%m-%d',
  hour: '%Y-%m-%d',
  minute: '%Y-%m-%d',
  second: '%Y-%m-%d',
  millisecond: '%Y-%m-%d'
};

const defaultTickerHighChartMessages: TickerHighChartMessages = {
  companyName: 'JANUS AI',
  all: 'All',
  from: 'From',
  to: 'To',
  zoom: 'Zoom',
  january: 'January',
  february: 'February',
  march: 'March',
  april: 'April',
  may: 'May',
  june: 'June',
  july: 'July',
  august: 'August',
  september: 'September',
  october: 'October',
  november: 'November',
  december: 'December',
  sunday: 'Sunday',
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  month: 'M',
  month3: '3M',
  month6: '6M',
  yearToDate: 'YTD',
  year1: '1Y'
};

const localeMessages = { zh, en } as const;

const getTickerHighChartMessages = (lang: LanguageCode): TickerHighChartMessages => {
  const localeModule = localeMessages[lang] as Record<string, any>;
  const rawMessages = localeModule.tickerHighChart ?? localeModule.highChart ?? {};

  return {
    ...defaultTickerHighChartMessages,
    ...rawMessages
  };
};

const buildBaseOptions = (messages: TickerHighChartMessages): Options => ({
  navigation: {
    iconsURL: '/highcharts/gfx/stock-icons/'
  },
  credits: {
    text: messages.companyName,
    href: undefined
  },
  rangeSelector: {
    inputEnabled: false,
    buttons: [
      {
        type: 'month',
        count: 1,
        text: messages.month
      },
      {
        type: 'month',
        count: 3,
        text: messages.month3
      },
      {
        type: 'month',
        count: 6,
        text: messages.month6
      },
      {
        type: 'ytd',
        text: messages.yearToDate
      },
      {
        type: 'year',
        count: 1,
        text: messages.year1
      },
      {
        type: 'all',
        text: messages.all
      }
    ],
    inputDateFormat: '%Y-%m-%d',
    inputEditDateFormat: '%Y-%m-%d',
    selected: 2
  },
  title: {
    text: ''
  },
  navigator: {
    xAxis: {
      labels: {
        formatter: function (this: AxisLabelsFormatterContextObject): string {
          return highcharts.dateFormat('%Y-%m', Number(this.value));
        }
      }
    }
  },
  time: {
    timezone: 'Asia/Shanghai'
  },
  lang: {
    rangeSelectorFrom: messages.from,
    rangeSelectorTo: messages.to,
    rangeSelectorZoom: messages.zoom,
    months: [
      messages.january,
      messages.february,
      messages.march,
      messages.april,
      messages.may,
      messages.june,
      messages.july,
      messages.august,
      messages.september,
      messages.october,
      messages.november,
      messages.december
    ],
    shortMonths: [
      messages.january,
      messages.february,
      messages.march,
      messages.april,
      messages.may,
      messages.june,
      messages.july,
      messages.august,
      messages.september,
      messages.october,
      messages.november,
      messages.december
    ],
    weekdays: [
      messages.sunday,
      messages.monday,
      messages.tuesday,
      messages.wednesday,
      messages.thursday,
      messages.friday,
      messages.saturday
    ]
  },
  colors: logoColors,
  tooltip: {
    xDateFormat: '%Y-%m-%d',
    dateTimeLabelFormats
  },
  xAxis: {
    dateTimeLabelFormats,
    labels: {
      rotation: -45,
      align: 'right'
    }
  }
});

const buildRuntimeChartOptions = (messages: TickerHighChartMessages): Options => {
  const baseOptions = buildBaseOptions(messages);

  return {
    credits: baseOptions.credits,
    rangeSelector: baseOptions.rangeSelector,
    lang: baseOptions.lang,
    tooltip: baseOptions.tooltip,
    xAxis: baseOptions.xAxis,
    navigator: baseOptions.navigator
  };
};

const applyRangeSelectorLocale = (chart: any, messages: TickerHighChartMessages) => {
  const rangeSelector = chart?.rangeSelector;
  chart.options.lang = {
    ...chart.options.lang,
    rangeSelectorZoom: messages.zoom,
    rangeSelectorFrom: messages.from,
    rangeSelectorTo: messages.to
  };

  rangeSelector?.zoomText?.attr?.({ text: messages.zoom });
  rangeSelector?.minLabel?.attr?.({ text: messages.from });
  rangeSelector?.maxLabel?.attr?.({ text: messages.to });

  if (rangeSelector?.dropdown?.options?.[0]) {
    rangeSelector.dropdown.options[0].text = messages.zoom;
  }

  rangeSelector?.render?.(chart.xAxis?.[0]?.min, chart.xAxis?.[0]?.max);
};

export const updateTickerHighchartLocale = (lang: LanguageCode = 'zh') => {
  const messages = getTickerHighChartMessages(lang);
  const baseOptions = buildBaseOptions(messages);

  highcharts.setOptions(baseOptions);

  highcharts.charts.forEach((chart) => {
    if (!chart) return;
    chart.update(buildRuntimeChartOptions(messages), true, true);
    applyRangeSelectorLocale(chart, messages);
  });
};

updateTickerHighchartLocale();

export const Highcharts = highcharts;
